import React, { useState, useEffect, useMemo } from "react";
import HomeView from "../Components/Dashboard/HomeView";
import RequestsView from "../Components/Dashboard/RequestsView";
import TicketsView from "../Components/Dashboard/TicketsView";
import PaymentsView from "../Components/Dashboard/PaymentsView";
import ProfileView from "../Components/Dashboard/ProfileView";

// Optimization: Constant defined outside component to prevent recreation on re-renders
const NAV_ITEMS = [
  { id: 'home', label: 'Overview', icon: 'dashboard' },
  { id: 'requests', label: 'My Plans', icon: 'event_note' },
  { id: 'tickets', label: 'Tickets', icon: 'confirmation_number' },
  { id: 'payments', label: 'Payments', icon: 'payments' },
  { id: 'profile', label: 'Profile', icon: 'person' },
];

const DashboardPage = () => {
  const [currentView, setCurrentView] = useState("home");
  const [selectedEvent, setSelectedEvent] = useState(null);

  // Scroll to top when view changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentView]);

  // Memoized content renderer to prevent unnecessary calculations
  const content = useMemo(() => {
    switch (currentView) {
      case 'home':
        return <HomeView onChangeView={setCurrentView} />;
      case 'requests':
        return <RequestsView onSelectEvent={(e) => { setSelectedEvent(e); setCurrentView('event-detail'); }} />;
      case 'tickets':
        return <TicketsView />;
      case 'payments':
        return <PaymentsView />;
      case 'profile':
        return <ProfileView />;
      case 'create':
        return (
          <div className="text-center pt-20 animate-fade-in-up">
            <p className="text-gray-500 font-medium">Plan Event Wizard Placeholder</p>
            <button onClick={() => setCurrentView('home')} className="mt-4 text-[#D4AF37] font-bold underline hover:text-[#b39020] transition-colors">Go Back</button>
          </div>
        );
      case 'notifications':
        return (
          <div className="text-center pt-20 animate-fade-in-up">
            <p className="text-gray-500 font-medium">Notification Center Placeholder</p>
            <button onClick={() => setCurrentView('home')} className="mt-4 text-[#D4AF37] font-bold underline hover:text-[#b39020] transition-colors">Go Back</button>
          </div>
        );
      default:
        return <HomeView onChangeView={setCurrentView} />;
    }
  }, [currentView, selectedEvent]);

  return (
    <div className="min-h-screen font-sans bg-gray-50 text-[#0B1F33] selection:bg-[#D4AF37] selection:text-[#0B1F33]">

      {/* --- Global Styles & Animations --- */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Material+Symbols+Outlined:opsz,wght,FILL,GRAD@20..48,100..700,0..1,-50..200');
        .hide-scrollbar::-webkit-scrollbar { display: none; }
        .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
        @keyframes fadeUp { from { opacity: 0; transform: translateY(15px); } to { opacity: 1; transform: translateY(0); } }
        .animate-fade-in-up { animation: fadeUp 0.4s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
        .btn-pulse { animation: pulse-gold 2s infinite; }
        @keyframes pulse-gold { 0% { box-shadow: 0 0 0 0 rgba(212, 175, 55, 0.4); } 70% { box-shadow: 0 0 0 10px rgba(212, 175, 55, 0); } 100% { box-shadow: 0 0 0 0 rgba(212, 175, 55, 0); } }
      `}</style>

      {/* --- Sticky Sub-Header Navigation --- */}
      <header className="sticky top-[80px] z-40 bg-[#0B1F33]/95 backdrop-blur-md border-t border-white/10 border-b border-[#D4AF37]/20 shadow-xl shadow-[#0B1F33]/5">
        <div className="w-[95%] md:w-[90%] mx-auto">
          <nav className="flex items-center gap-2 overflow-x-auto hide-scrollbar py-3">
            {NAV_ITEMS.map((item) => {
              const isActive = currentView === item.id;
              return (
                <button
                  key={item.id}
                  onClick={() => setCurrentView(item.id)}
                  className={`
                    group flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-bold whitespace-nowrap transition-all duration-300 border
                    ${isActive
                      ? "bg-gradient-to-r from-[#D4AF37]/20 to-[#D4AF37]/5 text-[#D4AF37] border-[#D4AF37]/30 shadow-[0_0_15px_rgba(212,175,55,0.1)]"
                      : "bg-transparent text-gray-400 border-transparent hover:bg-white/5 hover:text-gray-200"
                    }
                  `}
                >
                  <span className={`material-symbols-outlined text-lg transition-transform duration-300 ${isActive ? 'fill-current scale-110' : 'group-hover:scale-105'}`}>
                    {item.icon}
                  </span>
                  {item.label}
                </button>
              );
            })}
          </nav>
        </div>
      </header>

      {/* --- Main Content Area --- */}
      <main className="w-[95%] md:w-[90%] mx-auto py-8 md:py-12">
        {content}
      </main>

    </div>
  );
};

export default DashboardPage;